import { __assign } from 'tslib';
import React, { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import { List, CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import { toMap } from '../../../atom/util/reducer.js';
import Checkbox from '../../Checkbox/Checkbox.js';
import Chip from '../../../atom/component/Chip/Chip.js';
import Label from '../../../atom/component/Label/Label.js';
import MenuButton from '../../../atom/component/MenuButton/MenuButton.js';
import MenuOption from '../../../atom/component/MenuOption/MenuOption.js';
import Popper from '../../Popper/Popper.js';
import Search from '../Search/Search.js';
import clsx from 'clsx';
import useHAF from '../../../atom/hook/useHAF.js';
import Keys from '../../../atom/constant/keycodes.js';
import '../../../tailwind.css.js';

var options = {
    placement: 'bottom-start',
    modifiers: [
        { name: 'offset', options: { offset: [0, 4] } },
        { name: 'flip', enabled: false },
    ],
};
var newCache = function () { return new CellMeasurerCache({ fixedWidth: true, defaultHeight: 42, minHeight: 42 }); };
var MultiSelect = forwardRef(function (props, _ref) {
    var _a, _b;
    var ref = useHAF(_ref).ref;
    var _c = useState(newCache()), cache = _c[0], setCache = _c[1];
    var tagRef = useRef(null);
    var _d = useState({}), selection = _d[0], setSelection = _d[1];
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var _f = useState(''), query = _f[0], setQuery = _f[1];
    var _g = useState(336), menuHeight = _g[0], setMenuHeight = _g[1];
    var searchRef = useRef(null);
    var listRef = useRef(null);
    var focused = useRef();
    var items = useMemo(function () {
        if (!query || !props.search) {
            return props.items;
        }
        else if (typeof props.search === 'function') {
            return props.items.filter(function (_a) {
                var item = _a.item;
                return props.search(item, query);
            });
        }
        else {
            return props.items.filter(function (_a) {
                var label = _a.label;
                return label.toLowerCase().includes(query.toLowerCase());
            });
        }
    }, [query, props.search, props.items]);
    var allActive = useMemo(function () { return items.filter(function (_a) {
        var disabled = _a.disabled;
        return !disabled;
    }); }, [items]);
    var allSelected = useMemo(function () { return allActive.length === Object.keys(selection).length && allActive.every(function (_a) {
        var key = _a.key;
        return key in selection;
    }); }, [allActive, selection]);
    var toggleOption = function (ev, option) {
        var _a;
        var _b, _c, _d;
        if (ev.type === "click") {
            focused.current = searchRef.current;
            (_b = focused.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        if (selection[option.key]) {
            var updated = __assign({}, selection);
            delete updated[option.key];
            setSelection(updated);
            (_c = props.onChange) === null || _c === void 0 ? void 0 : _c.call(props, Object.values(updated).map(function (_a) {
                var item = _a.item;
                return item;
            }));
        }
        else {
            var updated = __assign(__assign({}, selection), (_a = {}, _a[option.key] = option, _a));
            setSelection(updated);
            (_d = props.onChange) === null || _d === void 0 ? void 0 : _d.call(props, Object.values(updated).map(function (_a) {
                var item = _a.item;
                return item;
            }));
        }
    };
    var toggleAll = function (ev) {
        var _a, _b, _c;
        if (ev.type === "click") {
            focused.current = searchRef.current;
            (_a = focused.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        if (allSelected) {
            setSelection({});
            (_b = props.onChange) === null || _b === void 0 ? void 0 : _b.call(props, []);
        }
        else {
            setSelection(allActive.reduce(toMap('key'), {}));
            (_c = props.onChange) === null || _c === void 0 ? void 0 : _c.call(props, allActive.map(function (_a) {
                var item = _a.item;
                return item;
            }));
        }
    };
    var onClick = function (ev) {
        var _a, _b;
        if (tagRef.current === ev.target || !!props.noTags || !!props.nestedTags || !((_a = tagRef.current) === null || _a === void 0 ? void 0 : _a.contains(ev.target))) {
            setOpen(!open);
        }
        (_b = props.onClick) === null || _b === void 0 ? void 0 : _b.call(props, ev);
    };
    var onBlur = function (ev) {
        var _a, _b, _c;
        if (!ev.relatedTarget || (!((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget)) && !((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.contains(ev.relatedTarget)))) {
            setOpen(false);
        }
    };
    var onSearch = function (_query) {
        var _a;
        (_a = props.onSearch) === null || _a === void 0 ? void 0 : _a.call(props, _query);
        if (props.search) {
            setQuery(_query);
        }
    };
    var onSearchBlur = function (ev) {
        var _a, _b, _c, _d;
        if (ev.type === "click" && (ev.target === searchRef.current && (((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(ev.relatedTarget)) || ((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.contains(ev.relatedTarget))))) {
            focused.current = searchRef.current;
            (_d = focused.current) === null || _d === void 0 ? void 0 : _d.focus();
        }
    };
    var text = function () {
        var classes = clsx({ 'text-sm placeholder:text-sm': (props.noTags || props.nestedTags) && props.size === 'sm' });
        switch (props.variant) {
            case 'secondary':
            case 'tertiary':
                return clsx(classes, {
                    'text-disabled-text': props.disabled,
                    'text-blue-301 hover:text-brand-medium active:text-blue-301': !props.disabled && open,
                    'text-brand hover:text-brand-medium active:text-blue-301': !props.disabled && !open,
                });
            case 'quaternary':
                return clsx(classes, {
                    'text-disabled-text': props.disabled,
                    'text-blue-301 hover:text-brand-medium active:text-blue-301': !props.disabled && open,
                    'text-dark-text hover:text-brand-medium active:text-blue-301': !props.disabled && !open,
                });
            default:
                return clsx(classes, {
                    'text-disabled-text': props.disabled,
                    'text-dark-text group-focus:text-blue-301': !props.disabled,
                });
        }
    };
    var renderRow = function (_a) {
        var _b;
        var index = _a.index; _a.key; var style = _a.style, parent = _a.parent;
        var option = items[index] || null;
        var nodeIdx = props.selectAllOption ? index + 1 : index;
        return !option ? null : (
        // @ts-ignore
        React.createElement(CellMeasurer, { key: nodeIdx, cache: cache, parent: parent, rowIndex: index, columnIndex: 0 },
            React.createElement(MenuOption, { tabIndex: 0, key: nodeIdx, "data-nodeidx": nodeIdx, wrap: props.wrap, disabled: option.disabled, selected: !!selection[option.key], onClick: function (ev) { return toggleOption(ev, option); }, onFocus: function (ev) { focused.current = ev.target; }, children: option.node || option.label, prefix: props.checkboxes ? React.createElement(Checkbox, { tabIndex: -1, disabled: props.disabled, checked: !!selection[option.key] }) : undefined, suffix: (_b = props.actions) === null || _b === void 0 ? void 0 : _b.map(function (action) { return action(option.item, option.idx || -1); }), style: style })));
    };
    var keyboardEventSource = function (ev) {
        var _a, _b, _c;
        if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(ev.target)) {
            return 'button';
        }
        else if ((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.contains(ev.target)) {
            return 'popper';
        }
        return null;
    };
    var handleButtonKeydown = function (ev) {
        var _a, _b, _c, _d, _e;
        switch (ev.code) {
            case Keys.ESCAPE: {
                if (open) {
                    setOpen(false);
                }
                break;
            }
            case Keys.SPACE: {
                if (ev.target === ref.current) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setOpen(!open);
                }
                break;
            }
            case Keys.ENTER: {
                ev.preventDefault();
                ev.stopPropagation();
                if (!open) {
                    setOpen(true);
                }
                break;
            }
            case Keys.ARROW_DOWN: {
                if (!open) {
                    setOpen(true);
                }
                else if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) {
                    var first = Array.from((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.nextElementSibling) === null || _c === void 0 ? void 0 : _c.querySelectorAll("[data-nodeidx]").values())[0];
                    focused.current = first;
                    (_d = focused.current) === null || _d === void 0 ? void 0 : _d.focus();
                }
                break;
            }
            case Keys.ARROW_UP: {
                if (open) {
                    setOpen(false);
                    focused.current = searchRef.current;
                    (_e = focused.current) === null || _e === void 0 ? void 0 : _e.focus();
                }
                break;
            }
        }
    };
    var handlePopperKeydown = function (ev) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        switch (ev.code) {
            case Keys.ESCAPE: {
                if (open) {
                    setOpen(false);
                }
                break;
            }
            case Keys.ENTER: {
                ev.preventDefault();
                ev.stopPropagation();
                if (((_a = ref.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) && ((_b = focused.current) === null || _b === void 0 ? void 0 : _b.hasAttribute("data-nodeidx"))) {
                    var idx = Number.parseInt((_c = focused.current) === null || _c === void 0 ? void 0 : _c.getAttribute("data-nodeidx"));
                    if (idx === 0 && props.selectAllOption) {
                        toggleAll(ev);
                    }
                    else {
                        var option = props.selectAllOption ? items[idx - 1] : items[idx];
                        toggleOption(ev, option);
                    }
                }
                break;
            }
            case Keys.ARROW_UP: {
                ev.preventDefault();
                ev.stopPropagation();
                if ((_d = ref.current) === null || _d === void 0 ? void 0 : _d.nextElementSibling) {
                    var nodes = Array.from((_f = (_e = ref.current) === null || _e === void 0 ? void 0 : _e.nextElementSibling) === null || _f === void 0 ? void 0 : _f.querySelectorAll("[data-nodeidx]").values()).reverse();
                    var idx_1 = Number.parseInt((_g = focused.current) === null || _g === void 0 ? void 0 : _g.getAttribute("data-nodeidx"));
                    var previousIdx = nodes.findIndex(function (node) { return Number.parseInt(node.getAttribute("data-nodeidx")) < idx_1; });
                    if (previousIdx > -1) {
                        focused.current = nodes[previousIdx];
                        (_h = focused.current) === null || _h === void 0 ? void 0 : _h.focus();
                    }
                    else if ((props.search || props.onSearch) && focused.current !== searchRef.current) {
                        focused.current = searchRef.current;
                        (_j = focused.current) === null || _j === void 0 ? void 0 : _j.focus();
                    }
                    else {
                        setOpen(false);
                        focused.current = ref.current;
                        (_k = focused.current) === null || _k === void 0 ? void 0 : _k.focus();
                    }
                }
                break;
            }
            case Keys.ARROW_DOWN: {
                ev.preventDefault();
                ev.stopPropagation();
                if ((_l = ref.current) === null || _l === void 0 ? void 0 : _l.nextElementSibling) {
                    var nodes = Array.from((_o = (_m = ref.current) === null || _m === void 0 ? void 0 : _m.nextElementSibling) === null || _o === void 0 ? void 0 : _o.querySelectorAll("[data-nodeidx]").values());
                    var idx_2 = Number.parseInt((_p = focused.current) === null || _p === void 0 ? void 0 : _p.getAttribute("data-nodeidx"));
                    var nextIdx = nodes.findIndex(function (node) { return Number.parseInt(node.getAttribute("data-nodeidx")) > idx_2; });
                    if (nextIdx > -1) {
                        focused.current = nodes[nextIdx];
                        (_q = focused.current) === null || _q === void 0 ? void 0 : _q.focus();
                    }
                    else {
                        var first = nodes[0];
                        focused.current = first;
                        (_r = focused.current) === null || _r === void 0 ? void 0 : _r.focus();
                    }
                }
                break;
            }
        }
    };
    var onKeyDown = function (ev) {
        switch (keyboardEventSource(ev)) {
            case 'button':
                handleButtonKeydown(ev);
                break;
            case 'popper':
                handlePopperKeydown(ev);
                break;
        }
    };
    useEffect(function () {
        var _a;
        setSelection(((_a = props.value) === null || _a === void 0 ? void 0 : _a.reduce(toMap('key'), {})) || {});
    }, [props.value]);
    useEffect(function () {
        setCache(newCache());
    }, [props.wrap, props.checkboxes, open, items]);
    useEffect(function () {
        var _a;
        (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.recomputeRowHeights();
        var defaultHeight = 336;
        var baselineHeight = 42 * items.length;
        var calculatedHeight = items
            .map(function (_, index) { return (cache === null || cache === void 0 ? void 0 : cache.rowHeight({ index: index })) || 42; })
            .reduce(function (x, y) { return x + y; }, 0);
        setMenuHeight(8 + Math.min(defaultHeight, Math.max(baselineHeight, calculatedHeight)));
    }, [cache]);
    useEffect(function () {
        var _a, _b;
        if (open) {
            focused.current = searchRef.current;
            (_a = focused.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            onSearch('');
            (_b = props.onClose) === null || _b === void 0 ? void 0 : _b.call(props);
        }
    }, [open]);
    return (React.createElement(React.Fragment, null,
        React.createElement(MenuButton, { ref: ref, id: props.id, name: props.name, open: open, onClick: onClick, onBlur: onBlur, disabled: props.disabled, error: props.error, width: props.width, variant: props.variant, className: props.className, onKeyDown: onKeyDown },
            React.createElement("div", { className: 'flex items-center justify-between w-[calc(100%_-_24px)]' }, !Object.keys(selection).length ? React.createElement(Label, { className: clsx('font-normal truncate', text()), label: props.placeholder }) :
                React.createElement("div", { ref: tagRef, className: 'flex flex-wrap py-[6px] truncate w-full' }, props.noTags || props.nestedTags ?
                    React.createElement(Label, { label: props.selectionPlaceholder || "".concat(Object.keys(selection).length, " Selected"), className: clsx('font-normal truncate', text()) }) :
                    Object.values(selection).map(function (option) { return React.createElement(Chip, { key: option.key, label: option.label, disabled: props.disabled, onClick: function (ev) { return toggleOption(ev, option); } }); })))),
        React.createElement(Popper, { anchor: ref, open: !props.disabled && open, onBlur: onBlur, onKeyDown: onKeyDown, options: props.options || options, zIndex: props.zIndex || 10 },
            React.createElement("div", { className: 'h-fit rounded-lg bg-white shadow-md overflow-hidden', style: { width: props.menuWidth || ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 336 } },
                !props.noTags && props.nestedTags &&
                    React.createElement("div", { className: 'flex flex-wrap mt-2 mx-2 p-2 min-h-[77px] border border-gray-204 rounded truncate' }, Object.values(selection).map(function (option) { return React.createElement(Chip, { key: option.key, label: option.label, disabled: props.disabled, onClick: function (ev) { return toggleOption(ev, option); } }); })),
                (!!props.search || !!props.onSearch) && React.createElement(Search, { ref: searchRef, placeholder: props.nestedPlaceholder, onSearch: onSearch, onBlur: onSearchBlur }),
                props.selectAllOption &&
                    React.createElement(MenuOption, { tabIndex: 0, "data-nodeidx": 0, wrap: props.wrap, disabled: allActive.length < 1, selected: allSelected, onClick: toggleAll, onFocus: function (ev) { focused.current = ev.target; }, children: typeof props.selectAllOption === 'string' ? props.selectAllOption : 'Select All', prefix: props.checkboxes ? React.createElement(Checkbox, { tabIndex: -1, disabled: props.disabled, checked: allSelected, indeterminate: !!Object.keys(selection).length }) : undefined }),
                // @ts-ignore
                React.createElement(List, { ref: listRef, width: props.menuWidth || ((_b = ref.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 336, height: props.menuHeight || menuHeight, rowHeight: cache.rowHeight, rowCount: items.length || 0, rowRenderer: renderRow, deferredMeasurementCache: cache, className: clsx("py-1 outline-none", { "mt-2": !props.selectAllOption && !props.noTags && props.nestedTags, "mt-1 border-t border-t-[#BFD4E7]": props.selectAllOption }) }),
                props.footer && typeof props.footer === 'function'
                    ? props.footer({ close: function () { return setOpen(false); }, setOpen: setOpen })
                    : props.footer))));
});

export { MultiSelect as default };
